import { OAuth2AuthCodePKCE } from '@bity/oauth2-auth-code-pkce'
import { getChatService, onChatServiceReady, type ChatService } from 'lib/ws/services/chat-service'
import { UIMessage, UIMessage_MessageType, UIMessageRole, UIMessagesServiceListRequest } from 'pbts/c1/api/conversations/v1/conversations'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import React, { useCallback, useEffect, useReducer, useState, useRef } from 'react'
import { sendMessageApi, listMessagesApi } from 'redux_toolkit/conversations'
import { v4 as uuidv4 } from 'uuid'

// ========== Types ==========

export type Role = UIMessageRole

export type OnFinishOptions = {
    finishReason?: 'stop' | 'length' | 'content-filter' | 'error' | 'unknown'
}

export type UseChatOptions = {
    conversationId?: string
    initialMessages?: UIMessage[]
    initialInput?: string
    onFinish?: (message: UIMessage, options: OnFinishOptions) => void
    onError?: (error: Error) => void
    authClient?: OAuth2AuthCodePKCE
}

export type UseChatReturn = {
    messages: UIMessage[]
    error: Error | undefined
    append: (message: UIMessage | { role: Role; content: string }) => Promise<string | undefined>
    reload: () => Promise<string | undefined>
    stop: () => void
    setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>
    input: string
    setInput: React.Dispatch<React.SetStateAction<string>>
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    handleSubmit: (event?: { preventDefault?: () => void }) => void
    status: ChatStatus
    conversationId: string
    /** Force status to ready (e.g., when A2UI surface is received) */
    forceReady: () => void
}

export type ChatStatus = 'ready' | 'error' | 'thinking'

type ChatState = {
    messages: UIMessage[]
    status: ChatStatus
    error?: Error
    conversationId: string
}

type ChatAction =
    | { type: 'ADD_MESSAGE'; message: UIMessage }
    | { type: 'UPDATE_MESSAGE'; message: UIMessage }
    | { type: 'REPLACE_MESSAGE'; oldId: string; newMessage: UIMessage }
    | { type: 'SET_MESSAGES'; messages: UIMessage[] }
    | { type: 'SET_ERROR'; error: Error | undefined }
    | { type: 'RESET_EXCEPT_USER_MESSAGES'; lastUserMessageIndex: number }
    | { type: 'SET_CONVERSATION_ID'; conversationId: string }
    | { type: 'SET_STATUS'; status: ChatStatus }

// ========== Utilities ==========

const createTimestamp = (): Timestamp => {
    const now = new Date()
    return Timestamp.create({
        seconds: Math.floor(now.getTime() / 1000),
        nanos: (now.getTime() % 1000) * 1_000_000,
    })
}

const timestampToNumber = (timestamp: Timestamp): number => {
    return timestamp.seconds * 1000 + Math.floor(timestamp.nanos / 1_000_000)
}

const sortMessagesByCreatedAt = (messages: UIMessage[]): UIMessage[] => {
    return [...messages].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0
        return timestampToNumber(a.createdAt) - timestampToNumber(b.createdAt)
    })
}

const isUserMessage = (message: UIMessage): boolean => {
    return message.role === UIMessageRole.UI_MESSAGE_ROLE_USER
}

const createUIMessage = (
    message: UIMessage | { role: Role; content: string },
    conversationId: string,
): UIMessage => {
    return 'id' in message
        ? message
        : {
            id: uuidv4(),
            role: message.role,
            content: message.content,
            createdAt: createTimestamp(),
            conversationId,
            type: UIMessage_MessageType.CONTENT,
            blocks: [
                {
                    parts: {
                        oneofKind: 'textBlock',
                        textBlock: {
                            value: message.content,
                        },
                    },
                },
            ],
        }
}

// ========== State Management ==========

function chatReducer(state: ChatState, action: ChatAction): ChatState {
    switch (action.type) {
        case 'ADD_MESSAGE': {
            if (action.message.type === UIMessage_MessageType.DEBUG) {
                return state
            }
            // Receipt order is not guaranteed to match createdAt order
            // Any msg recieved with a later createdAt than the last msg will be skipped
            if (state.messages.length > 0) {
                const lastMsg = state.messages[state.messages.length - 1]
                const currentMsg = action.message
                if (
                    currentMsg.createdAt &&
                    lastMsg.createdAt &&
                    timestampToNumber(currentMsg.createdAt) <
                    timestampToNumber(lastMsg.createdAt)
                ) {
                    return state
                }
            }

            // At this point, once we have ensured msg order, we can trust the msg to determine the chat status
            let status: ChatStatus = 'ready'
            if (action.message.type === UIMessage_MessageType.THINKING) {
                // If the current msg being added is a thinking msg we expect to continue in a thinking state until a content msg is added
                // Also, continue to be 'thinking' while debug messages come in
                status = 'thinking'
            } else if (action.message.role === UIMessageRole.UI_MESSAGE_ROLE_USER) {
                // If the current msg being added is a user msg we are in a thinking state
                // The user is waiting for the agent to respond
                status = 'thinking'
            }

            return {
                ...state,
                status,
                messages: [...state.messages, action.message],
            }
        }
        case 'UPDATE_MESSAGE':
            return {
                ...state,
                messages: state.messages.map((msg) =>
                    (msg.id === action.message.id ? action.message : msg)),
            }

        case 'REPLACE_MESSAGE':
            return {
                ...state,
                messages: state.messages.map((msg) =>
                    (msg.id === action.oldId ? action.newMessage : msg)),
            }

        case 'SET_MESSAGES':
            return {
                ...state,
                // This is a unique case
                // If there is only one message, it will be the user's fist message and they are waiting for the agent to respond
                status: action.messages.length === 1 ? 'thinking' : 'ready',
                messages: action.messages.filter((msg) => msg.conversationId === state.conversationId),
            }

        case 'SET_CONVERSATION_ID':
            return {
                ...state,
                conversationId: action.conversationId,
                messages: [],
                // If no messages, we are in a fresh convo with a thinking status, waiting for the first message
                // If there are messages, we are in a ready state, waiting for the user to submit a message again
                status: 'ready',
                error: undefined,
            }

        case 'SET_ERROR':
            return {
                ...state,
                error: action.error,
                status: action.error ? 'error' : state.status,
            }

        case 'RESET_EXCEPT_USER_MESSAGES':
            return {
                ...state,
                messages: state.messages.filter((msg, index) =>
                    isUserMessage(msg) || index <= action.lastUserMessageIndex),
                error: undefined,
            }

        case 'SET_STATUS':
            return {
                ...state,
                status: action.status,
            }

        default:
            return state
    }
}

// ========== Main Hook ==========

/**
 * Hook for managing chat conversations with real-time messaging.
 *
 * Two usage patterns:
 * 1. Existing conversation: Pass conversationId to connect to an existing chat
 * 2. Wait for conversation: Pass undefined conversationId, hook remains idle until ID is provided
 *
 * Note: For creating new conversations, create the conversation first via API,
 * then pass the returned ID to this hook.
 */
export const useChat = (options: UseChatOptions = {}): UseChatReturn => {
    const {
        conversationId,
        initialMessages = [],
        initialInput = '',
        onFinish,
        onError: _onError,
    } = options

    // Use a stable ID for the return value when no conversationId is provided
    // This prevents the return value from changing on every render
    const stableIdRef = useRef<string>(uuidv4())
    const effectiveConversationId = conversationId ?? stableIdRef.current
    const isEnabled = !!conversationId

    // ========== State ==========
    const [input, setInput] = useState<string>(initialInput)
    const [state, dispatch] = useReducer(chatReducer, {
        messages: isEnabled ? initialMessages.filter((msg) => msg.conversationId === conversationId) : [],
        status: 'ready',
        error: undefined,
        conversationId: effectiveConversationId,
    })

    // ========== Refs ==========
    const isInitializedRef = useRef(false)
    const currentConversationIdRef = useRef<string>('')
    const messagesRef = useRef<UIMessage[]>(state.messages)

    useEffect(() => {
        messagesRef.current = state.messages
    }, [state.messages])

    // ========== API & WebSocket ==========
    // Use state to reactively get chat service when it becomes available
    // (handles case where component mounts before auth is set)
    const [chatService, setChatService] = useState<ChatService | undefined>(() => getChatService())

    useEffect(() => {
        // Subscribe to be notified when chat service becomes available
        return onChatServiceReady((service) => {
            setChatService(service)
        })
    }, [])

    const { mutate: sendMessage } = sendMessageApi()

    const {
        data: fetchedMessages,
        isLoading: isLoadingMessages,
    } = listMessagesApi.useList(UIMessagesServiceListRequest.create({
        conversationId: conversationId ?? '',
    }), {
        pageSize: 100,
        actionable: isEnabled,
    })

    // ========== Event Handlers ==========
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setInput(e.target.value)
    }, [])

    const handleError = useCallback((error: Error): void => {
        console.error('[useChat] Error:', error.message)
        dispatch({ type: 'SET_ERROR', error })
        _onError?.(error)
    }, [_onError])

    const handleMessage = useCallback((message: UIMessage): void => {
        const messageExists = messagesRef.current.some((msg) => msg.id === message.id)

        dispatch({
            type: messageExists ? 'UPDATE_MESSAGE' : 'ADD_MESSAGE',
            message,
        })

        // Only set status to ready when we receive an assistant message
        if (message.role === UIMessageRole.UI_MESSAGE_ROLE_ASSISTANT && message.type === UIMessage_MessageType.CONTENT) {
            onFinish?.(message, { finishReason: 'stop' })
        }
    }, [onFinish])

    // ========== Core Functions ==========

    /**
     * Append a message to the conversation
     */
    const append = useCallback(async (
        message: UIMessage | { role: Role; content: string },
    ): Promise<string | undefined> => {
        try {
            dispatch({ type: 'SET_ERROR', error: undefined })

            const newMessage = createUIMessage(message, state.conversationId)

            if (isUserMessage(newMessage)) {
                // Add optimistic user message
                const messageExists = messagesRef.current.some((msg) => msg.id === newMessage.id)
                if (!messageExists) {
                    dispatch({ type: 'ADD_MESSAGE', message: newMessage })
                }

                try {
                    // Send message via API
                    const response = await sendMessage({
                        conversationId: state.conversationId,
                        message: newMessage.content,
                    })

                    // Update with server response
                    const updatedMessage: UIMessage = {
                        ...newMessage,
                        id: response.data?.id || newMessage.id,
                        createdAt: response.data?.createdAt || newMessage.createdAt,
                    }

                    dispatch({
                        type: 'REPLACE_MESSAGE',
                        oldId: newMessage.id,
                        newMessage: updatedMessage,
                    })
                } catch (apiError) {
                    const error = apiError instanceof Error ? apiError : new Error(String(apiError))
                    handleError(error)
                }
            } else {
                dispatch({ type: 'ADD_MESSAGE', message: newMessage })
                // Only set status to ready if this is an assistant message
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            handleError(error)
        }
        return undefined
    }, [state.conversationId, sendMessage, handleError])

    /**
     * Reload conversation from the last user message
     */
    const reload = useCallback(async (): Promise<string | undefined> => {
        const lastUserMessageIndex = [...messagesRef.current]
            .reverse()
            .findIndex((m) => isUserMessage(m))

        if (lastUserMessageIndex === -1) return undefined

        const userMessageIndex = messagesRef.current.length - 1 - lastUserMessageIndex
        const lastUserMessage = messagesRef.current[userMessageIndex]

        dispatch({
            type: 'RESET_EXCEPT_USER_MESSAGES',
            lastUserMessageIndex: userMessageIndex,
        })

        return append(lastUserMessage)
    }, [append])

    /**
     * Stop the current streaming operation
     */
    const stop = useCallback((): void => {
        // TBD: Implement stop functionality
    }, [])

    /**
     * Handle form submission
     */
    const handleSubmit = useCallback((event?: { preventDefault?: () => void }): void => {
        event?.preventDefault?.()
        if (!input.trim()) return

        const userMessage = {
            role: UIMessageRole.UI_MESSAGE_ROLE_USER as Role,
            content: input,
        }

        setInput('')
        append(userMessage)
    }, [input, append])

    /**
     * Update messages array
     */
    const setMessages = useCallback((
        messagesOrSetter: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
    ): void => {
        const messages = typeof messagesOrSetter === 'function'
            ? messagesOrSetter(messagesRef.current)
            : messagesOrSetter

        dispatch({ type: 'SET_MESSAGES', messages })
    }, [])

    /**
     * Force status to ready (used when A2UI surfaces are received)
     */
    const forceReady = useCallback((): void => {
        dispatch({ type: 'SET_STATUS', status: 'ready' })
    }, [])

    // ========== Effects ==========
    /**
     * Update conversation when conversationId changes - only when enabled
     */
    useEffect(() => {
        if (!isEnabled || !conversationId) return
        if (currentConversationIdRef.current === conversationId) return

        currentConversationIdRef.current = conversationId
        isInitializedRef.current = true

        dispatch({ type: 'SET_CONVERSATION_ID', conversationId })
    }, [isEnabled, conversationId])

    /**
     * Load messages when fetched - only when enabled
     */
    useEffect(() => {
        if (!isEnabled) return
        if (!isLoadingMessages && fetchedMessages && fetchedMessages.length > 0) {
            const sortedMessages = sortMessagesByCreatedAt(fetchedMessages.flat())
            dispatch({ type: 'SET_MESSAGES', messages: sortedMessages })
        }
    }, [isEnabled, fetchedMessages, conversationId, isLoadingMessages])

    /**
     * Handle websocket messages - only subscribe when we have a real conversationId
     */
    useEffect(() => {
        if (!chatService || !isEnabled || !conversationId) return

        void chatService.subscribe(conversationId, handleMessage)
        return (): void => chatService.unsubscribe(conversationId, handleMessage)
    }, [chatService, isEnabled, conversationId, handleMessage])

    // ========== Return ==========
    return {
        messages: state.messages,
        error: state.error,
        append,
        reload,
        stop,
        setMessages,
        input,
        setInput,
        handleInputChange,
        handleSubmit,
        status: state.status,
        conversationId: state.conversationId,
        forceReady,
    }
}
